import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "./DashboardLayout";
import { deleteVolunteer, fetchVolunteers, updateVolunteer, VolunteerRow } from "./api";
import ActionToast, { ToastAction } from "./ActionToast";

const GENDER_OPTIONS = ["Male", "Female", "Other"];
const SKILL_OPTIONS = ["Technical", "Teaching", "IT", "Others"];
const AVAILABILITY_OPTIONS = ["DAY", "Hours", "Weekend"];
const VOLUNTEERING_AREA_OPTIONS = [
  "Education",
  "Women Empowerment",
  "Environment",
  "Health",
  "Livelihood",
  "Fundraising",
  "Administrator",
];
const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

const VolunteerList: React.FC = () => {
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
  const [rows, setRows] = useState<VolunteerRow[]>([]);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [viewRow, setViewRow] = useState<VolunteerRow | null>(null);
  const [editRow, setEditRow] = useState<VolunteerRow | null>(null);
  const [editSkills, setEditSkills] = useState<string[]>([]);
  const [showStateOptions, setShowStateOptions] = useState(false);
  const [stateSearchValue, setStateSearchValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [photoDragOver, setPhotoDragOver] = useState(false);
  const [toastAction, setToastAction] = useState<ToastAction | null>(null);

  useEffect(() => {
    fetchVolunteers()
      .then(setRows)
      .catch((err: Error) => setError(err.message || "Failed to fetch volunteers"));
  }, []);

  useEffect(() => {
    if (!toastAction) return;
    const timer = setTimeout(() => setToastAction(null), 6500);
    return () => clearTimeout(timer);
  }, [toastAction]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.fullName?.toLowerCase().includes(q) ||
        r.email?.toLowerCase().includes(q) ||
        r.skills?.toLowerCase().includes(q)
    );
  }, [rows, search]);

  const getInitials = (name: string) =>
    name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || "")
      .join("");

  const normalizeSkills = (raw?: string) => {
    if (!raw) return "";
    const value = String(raw).trim();
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item).trim()).filter(Boolean).join(", ");
      }
    } catch {
      // fall through to text normalization
    }
    return value
      .replace(/^\[/, "")
      .replace(/\]$/, "")
      .replace(/"/g, "")
      .replace(/'/g, "")
      .trim();
  };

  const formatDisplayDate = (value?: string) => {
    if (!value) return "-";
    const raw = String(value).trim();

    if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) {
      return raw;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [year, month, day] = raw.split("-");
      return `${day}-${month}-${year}`;
    }

    if (raw.includes("T")) {
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return raw;
      return d.toLocaleDateString("en-GB", { timeZone: "Asia/Kolkata" });
    }

    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = String(d.getFullYear());
    return `${day}-${month}-${year}`;
  };

  const toInputDate = (value?: string) => {
    if (!value) return "";
    const raw = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) {
      const [day, month, year] = raw.split("-");
      return `${year}-${month}-${day}`;
    }
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const parseSkillsToArray = (raw?: string) => {
    if (!raw) return [];
    const text = String(raw).trim();
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item).trim()).filter(Boolean);
      }
    } catch {
      // fall through
    }
    return text
      .replace(/^\[/, "")
      .replace(/\]$/, "")
      .replace(/"/g, "")
      .replace(/'/g, "")
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const normalizeFromOptions = (value: string | undefined, options: string[]) => {
    const raw = (value || "").trim();
    if (!raw) return "";
    const found = options.find((option) => option.toLowerCase() === raw.toLowerCase());
    return found || raw;
  };

  const stateOptions = useMemo(() => {
    const merged = [...INDIAN_STATES, ...rows.map((r) => (r.state || "").trim()), (editRow?.state || "").trim()].filter(Boolean);
    return Array.from(new Set(merged)).sort((a, b) => a.localeCompare(b));
  }, [rows, editRow?.state]);

  const filteredStateOptions = useMemo(() => {
    const q = stateSearchValue.trim().toLowerCase();
    if (!q) return stateOptions;
    return stateOptions.filter((option) => option.toLowerCase().includes(q));
  }, [stateOptions, stateSearchValue]);

  const skillOptions = useMemo(() => {
    const merged = [...SKILL_OPTIONS, ...editSkills].filter(Boolean);
    return Array.from(new Set(merged));
  }, [editSkills]);

  const onDelete = async (id: number) => {
    if (!window.confirm("Delete this volunteer?")) return;
    try {
      await deleteVolunteer(id);
      setRows((prev) => prev.filter((row) => row.id !== id));
      setToastAction("deleted");
    } catch (err: any) {
      alert(err.message || "Delete failed");
    }
  };

  const onSave = async () => {
    if (!editRow) return;
    const hasValidState = stateOptions.some((option) => option.toLowerCase() === (editRow.state || "").trim().toLowerCase());
    if (!hasValidState) {
      alert("Please select a valid state from the dropdown.");
      return;
    }
    if (editSkills.length === 0) {
      alert("Please select at least one skill.");
      return;
    }
    const hasOthersSkill = editSkills.some((skill) => skill.toLowerCase() === "others");
    const trimmedOtherSkill = (editRow.otherSkill || "").trim();
    if (hasOthersSkill && !trimmedOtherSkill) {
      alert("Please specify other skill.");
      return;
    }

    const rowToSave: VolunteerRow = {
      ...editRow,
      skills: JSON.stringify(editSkills),
      otherSkill: hasOthersSkill ? trimmedOtherSkill : "",
    };

    setSaving(true);
    try {
      await updateVolunteer(rowToSave);
      setRows((prev) => prev.map((item) => (item.id === rowToSave.id ? { ...item, ...rowToSave } : item)));
      setEditRow(null);
      setEditSkills([]);
      setStateSearchValue("");
      setShowStateOptions(false);
      setToastAction("saved");
    } catch (err: any) {
      alert(err.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const toMediaUrl = (value?: string) => {
    if (!value) return "";
    if (value.startsWith("http://") || value.startsWith("https://")) return value;
    return `${API_URL}${value}`;
  };

  return (
    <DashboardLayout>
      <ActionToast action={toastAction} />
      <h1 className="list-title">Volunteers</h1>
      <p className="list-subtitle">{rows.length} registered volunteers</p>
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="list-toolbar">
        <div className="search-wrap">
          <i className="fa-solid fa-magnifying-glass search-icon" />
          <input
            className="form-control search-input search-input-medium"
            placeholder="Search by name, email, or skill..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>
      <div className="table-shell table-responsive">
        <table className="table data-table mb-0">
          <thead>
            <tr>
              <th style={{ width: 70 }} />
              <th>Name</th>
              <th>Mobile</th>
              <th>Email</th>
              <th>Skills</th>
              <th>Availability</th>
              <th>City/State</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => (
              <tr key={row.id}>
                <td>
                  <span className="table-avatar">{getInitials(row.fullName || "")}</span>
                </td>
                <td>{row.fullName}</td>
                <td>{row.mobile}</td>
                <td>{row.email}</td>
                <td>
                  <div className="pill-wrap">
                    {normalizeSkills(row.skills)
                      .split(",")
                      .map((skill) => skill.trim())
                      .filter(Boolean)
                      .map((skill) => (
                        <span className="data-pill" key={`${row.id}-${skill}`}>
                          {skill}
                        </span>
                      ))}
                  </div>
                </td>
                <td>
                  <span className="availability-pill">{row.availability || "-"}</span>
                </td>
                <td>
                  {row.city}, {row.state}
                </td>
                <td className="text-end">
                  <div className="action-icons">
                    <button type="button" className="icon-btn" title="View Details" onClick={() => setViewRow(row)}>
                      <i className="fa-regular fa-eye" />
                    </button>
                    <button
                      type="button"
                      className="icon-btn"
                      title="Edit"
                      onClick={() => {
                        setEditRow({ ...row, skills: normalizeSkills(row.skills) });
                        setEditSkills(parseSkillsToArray(row.skills));
                        setStateSearchValue(row.state || "");
                        setShowStateOptions(false);
                      }}
                    >
                      <i className="fa-regular fa-pen-to-square" />
                    </button>
                    <button type="button" className="icon-btn icon-btn-danger" title="Delete" onClick={() => onDelete(row.id)}>
                      <i className="fa-regular fa-trash-can" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center py-4 text-muted">
                  No volunteers found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {viewRow && (
        <div className="dash-modal-overlay" onClick={() => setViewRow(null)}>
          <div className="dash-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dash-modal-header">
              <h3 className="dash-modal-title">Volunteer Details</h3>
              <button type="button" className="dash-modal-close" onClick={() => setViewRow(null)}>
                x
              </button>
            </div>
            <div className="dash-modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <div className="detail-label">Photo</div>
                  <div className="detail-value">
                    {viewRow.photoUrl ? (
                      <img
                        src={toMediaUrl(viewRow.photoUrl)}
                        alt={viewRow.fullName || "Volunteer"}
                        style={{ width: "96px", height: "96px", objectFit: "cover", borderRadius: "10px", border: "1px solid #dfe5ef" }}
                      />
                    ) : (
                      "-"
                    )}
                  </div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Full Name</div>
                  <div className="detail-value">{viewRow.fullName || "-"}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Mobile</div>
                  <div className="detail-value">{viewRow.mobile || "-"}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Email</div>
                  <div className="detail-value">{viewRow.email || "-"}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Aadhar (Optional)</div>
                  <div className="detail-value">{viewRow.aadhar || "-"}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Gender</div>
                  <div className="detail-value">{viewRow.gender || "-"}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Date of Birth</div>
                  <div className="detail-value">{formatDisplayDate(viewRow.dateOfBirth)}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">State</div>
                  <div className="detail-value">{viewRow.state || "-"}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">City</div>
                  <div className="detail-value">{viewRow.city || "-"}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Pin Code</div>
                  <div className="detail-value">{viewRow.pinCode || "-"}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Address</div>
                  <div className="detail-value">{viewRow.address || "-"}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Skills & Expertise</div>
                  <div className="detail-value">{normalizeSkills(viewRow.skills) || "-"}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Availability</div>
                  <div className="detail-value">{viewRow.availability || "-"}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Preferred Volunteering Area</div>
                  <div className="detail-value">{viewRow.volunteeringArea || "-"}</div>
                </div>
                <div className="detail-item">
                  <div className="detail-label">Previous Volunteer Experience</div>
                  <div className="detail-value">{viewRow.previousExperience || "-"}</div>
                </div>
              </div>
            </div>
            <div className="dash-modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setViewRow(null)}>
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setEditRow({ ...viewRow, skills: normalizeSkills(viewRow.skills) });
                  setEditSkills(parseSkillsToArray(viewRow.skills));
                  setStateSearchValue(viewRow.state || "");
                  setShowStateOptions(false);
                  setViewRow(null);
                }}
              >
                Edit Details
              </button>
            </div>
          </div>
        </div>
      )}

      {editRow && (
        <div className="dash-modal-overlay" onClick={() => setEditRow(null)}>
          <div className="dash-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dash-modal-header">
              <h3 className="dash-modal-title">Edit Volunteer</h3>
              <button type="button" className="dash-modal-close" onClick={() => setEditRow(null)}>
                x
              </button>
            </div>
            <div className="dash-modal-body">
              <div className="edit-grid">
                <div className="edit-field">
                  <label className="edit-label">Full Name</label>
                  <input className="form-control" placeholder="Enter full name" value={editRow.fullName || ""} onChange={(e) => setEditRow({ ...editRow, fullName: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Mobile</label>
                  <input className="form-control" placeholder="Enter mobile number" value={editRow.mobile || ""} onChange={(e) => setEditRow({ ...editRow, mobile: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Email</label>
                  <input className="form-control" placeholder="Enter email address" value={editRow.email || ""} onChange={(e) => setEditRow({ ...editRow, email: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Aadhar (Optional)</label>
                  <input className="form-control" placeholder="Enter Aadhar number" value={editRow.aadhar || ""} onChange={(e) => setEditRow({ ...editRow, aadhar: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Gender</label>
                  <select className="form-select" value={normalizeFromOptions(editRow.gender, GENDER_OPTIONS)} onChange={(e) => setEditRow({ ...editRow, gender: e.target.value })}>
                    <option value="">Select Gender</option>
                    {GENDER_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                    {normalizeFromOptions(editRow.gender, GENDER_OPTIONS) && !GENDER_OPTIONS.includes(normalizeFromOptions(editRow.gender, GENDER_OPTIONS)) && (
                      <option value={normalizeFromOptions(editRow.gender, GENDER_OPTIONS)}>{normalizeFromOptions(editRow.gender, GENDER_OPTIONS)}</option>
                    )}
                  </select>
                </div>
                <div className="edit-field">
                  <label className="edit-label">Date of Birth</label>
                  <input className="form-control" type="date" placeholder="Select date of birth" value={toInputDate(editRow.dateOfBirth)} onChange={(e) => setEditRow({ ...editRow, dateOfBirth: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">State</label>
                  <div style={{ position: "relative" }}>
                    <input
                      className="form-control"
                      placeholder="Search or select state"
                      value={stateSearchValue}
                      onFocus={() => setShowStateOptions(true)}
                      onChange={(e) => {
                        setStateSearchValue(e.target.value);
                        setShowStateOptions(true);
                      }}
                      onBlur={() => {
                        const matched = stateOptions.find((option) => option.toLowerCase() === stateSearchValue.trim().toLowerCase());
                        if (matched) {
                          setStateSearchValue(matched);
                          setEditRow({ ...editRow, state: matched });
                        } else {
                          setStateSearchValue(editRow.state || "");
                        }
                        setTimeout(() => setShowStateOptions(false), 120);
                      }}
                    />
                    {showStateOptions && filteredStateOptions.length > 0 && (
                      <div
                        className="list-group"
                        style={{
                          position: "absolute",
                          zIndex: 20,
                          width: "100%",
                          maxHeight: "180px",
                          overflowY: "auto",
                          border: "1px solid #dfe5ef",
                          borderRadius: "8px",
                          marginTop: "6px",
                          background: "#fff",
                        }}
                      >
                        {filteredStateOptions.slice(0, 50).map((option) => (
                          <button
                            key={option}
                            type="button"
                            className="list-group-item list-group-item-action"
                            onMouseDown={(evt) => evt.preventDefault()}
                            onClick={() => {
                              setStateSearchValue(option);
                              setEditRow({ ...editRow, state: option });
                              setShowStateOptions(false);
                            }}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="edit-field">
                  <label className="edit-label">City</label>
                  <input className="form-control" placeholder="Enter city" value={editRow.city || ""} onChange={(e) => setEditRow({ ...editRow, city: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Pin Code</label>
                  <input className="form-control" placeholder="Enter pin code" value={editRow.pinCode || ""} onChange={(e) => setEditRow({ ...editRow, pinCode: e.target.value })} />
                </div>
                <div className="edit-field full">
                  <label className="edit-label">Address</label>
                  <input className="form-control" placeholder="Enter full address" value={editRow.address || ""} onChange={(e) => setEditRow({ ...editRow, address: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Skills & Expertise</label>
                  <select
                    className="form-select"
                    multiple
                    value={editSkills}
                    onChange={(e) => {
                      const values = Array.from(e.currentTarget.selectedOptions, (option) => option.value);
                      setEditSkills(values);
                      if (!values.includes("Others")) {
                        setEditRow({ ...editRow, otherSkill: "" });
                      }
                    }}
                  >
                    {skillOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  {editSkills.includes("Others") && (
                    <input
                      className="form-control mt-2"
                      placeholder="Specify other skill"
                      value={editRow.otherSkill || ""}
                      onChange={(e) => setEditRow({ ...editRow, otherSkill: e.target.value })}
                    />
                  )}
                </div>
                <div className="edit-field">
                  <label className="edit-label">Availability</label>
                  <select className="form-select" value={normalizeFromOptions(editRow.availability, AVAILABILITY_OPTIONS)} onChange={(e) => setEditRow({ ...editRow, availability: e.target.value })}>
                    <option value="">Select Availability</option>
                    {AVAILABILITY_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option === "DAY" ? "Day" : option}
                      </option>
                    ))}
                    {normalizeFromOptions(editRow.availability, AVAILABILITY_OPTIONS) &&
                      !AVAILABILITY_OPTIONS.includes(normalizeFromOptions(editRow.availability, AVAILABILITY_OPTIONS)) && (
                        <option value={normalizeFromOptions(editRow.availability, AVAILABILITY_OPTIONS)}>
                          {normalizeFromOptions(editRow.availability, AVAILABILITY_OPTIONS)}
                        </option>
                      )}
                  </select>
                </div>
                <div className="edit-field">
                  <label className="edit-label">Preferred Volunteering Area</label>
                  <select className="form-select" value={normalizeFromOptions(editRow.volunteeringArea, VOLUNTEERING_AREA_OPTIONS)} onChange={(e) => setEditRow({ ...editRow, volunteeringArea: e.target.value })}>
                    <option value="">Select Area</option>
                    {VOLUNTEERING_AREA_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                    {normalizeFromOptions(editRow.volunteeringArea, VOLUNTEERING_AREA_OPTIONS) &&
                      !VOLUNTEERING_AREA_OPTIONS.includes(normalizeFromOptions(editRow.volunteeringArea, VOLUNTEERING_AREA_OPTIONS)) && (
                        <option value={normalizeFromOptions(editRow.volunteeringArea, VOLUNTEERING_AREA_OPTIONS)}>
                          {normalizeFromOptions(editRow.volunteeringArea, VOLUNTEERING_AREA_OPTIONS)}
                        </option>
                      )}
                  </select>
                </div>
                <div className="edit-field">
                  <label className="edit-label">Previous Volunteer Experience</label>
                  <input className="form-control" placeholder="Enter previous experience" value={editRow.previousExperience || ""} onChange={(e) => setEditRow({ ...editRow, previousExperience: e.target.value })} />
                </div>
                <div className="edit-field full">
                  <label className="edit-label">Photo Upload</label>
                  <label
                    className={`dash-upload-box ${photoDragOver ? "is-dragover" : ""}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setPhotoDragOver(true);
                    }}
                    onDragLeave={() => setPhotoDragOver(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setPhotoDragOver(false);
                      const file = e.dataTransfer.files?.[0];
                      if (file) setEditRow({ ...editRow, photoFile: file });
                    }}
                  >
                    <i className="fa-regular fa-image" />
                    <input
                      type="file"
                      accept="image/*"
                      className="dash-upload-input"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setEditRow({ ...editRow, photoFile: file });
                      }}
                    />
                  </label>
                </div>
                <div className="edit-field full">
                  <label className="edit-label">Experience Details</label>
                  <textarea className="form-control" rows={3} placeholder="Enter experience details" value={editRow.experienceDetails || ""} onChange={(e) => setEditRow({ ...editRow, experienceDetails: e.target.value })} />
                </div>
              </div>
            </div>
            <div className="dash-modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setEditRow(null)}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" disabled={saving} onClick={onSave}>
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default VolunteerList;
